import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import { stashSay } from '../../../../../shared-web/say-handoff';
import { noteVisitorConsent, useVisitorConsent } from '../../../../../shared-web/inbound-door';
import { emberApi } from '../api';
import { navigate } from '../router';
import { useTellingDoors } from '../talk-sheet';
import { EmberDisc } from '../components/EmberDisc';
import { CONSENT_NOTE, CONSENT_TITLE } from '../components/ConsentTerms';
import './Talk.css';

/**
 * Where Ember games hands a person to Ember (2026-10-22 jam for the quiz;
 * 2026-10-23 the yard joins, so the one page takes the line and the words
 * rather than knowing the games). The link carries only which game and which
 * door; the first line is written by the game's handoff module
 * (shared/quiz-handoff.js, shared/yard-handoff.js; since 2026-10-27 also the
 * self-check pages', shared/check-handoff.js; since 2026-10-30 Gas Me Up's,
 * shared/gas-me-up-handoff.js) and carried into the chat
 * by handle like the homepage's sentence (say-handoff.ts).
 *
 * A signed-out browser that has not ticked 18+ and the Terms is asked here,
 * on this page, before the chat opens — the chat itself would send it back to
 * the door and the line would be lost. A spent free budget goes to the same
 * registration door every other way in does.
 */
export function GamesTalkHandoff({ line, draft = '', discovery }: {
  /** The conversation's first line, in the person's own voice; '' when the link named nothing we know. */
  line: string;
  /** Words for the chat's field instead, sent by the person themselves (the yard's own starred line, 2026-10-31); '' for none. */
  draft?: string;
  /** Which game (or self-check page) the person came from, on the consent gate's events (`data-log-discovery`). */
  discovery: 'quiz' | 'yard' | 'check' | 'gas_me_up';
}): ReactElement {
  const { status } = useSession();
  const consent = useVisitorConsent();
  const { spent, chatTo, registerTo } = useTellingDoors({ returnTo: '/' });
  const [ageOk, setAgeOk] = useState(false);
  const [tosOk, setTosOk] = useState(false);
  const left = useRef(false);

  const settling = status === 'loading' || (status === 'anonymous' && !consent.known);
  const asking = status === 'anonymous' && consent.known && !consent.consented && !spent;

  const go = (): void => {
    if (left.current) return;
    left.current = true;
    const handle = !spent && line ? stashSay(line) : '';
    if (!spent && line && !handle) trackFailed('talk_handoff', 'storage_unavailable');
    // The yard's own starred words wait in this browser like the line does — never in the address (say-handoff.ts).
    const draftHandle = draft ? stashSay(draft) : '';
    if (draft && !draftHandle) trackFailed('talk_handoff', 'storage_unavailable');
    const withDraft = draftHandle ? `${chatTo}&draft_say=${encodeURIComponent(draftHandle)}` : chatTo;
    const to = spent ? registerTo : handle ? `${withDraft}&say=${encodeURIComponent(handle)}` : withDraft;
    // On the next tick: a signed-in person leaves from this page's first
    // effect, which runs before the app's router has subscribed to moves —
    // navigating there changed the address and left this page on screen
    // (dev e2e 2026-10-22: a blank page).
    window.setTimeout(() => navigate(to, { replace: true }), 0);
  };

  useEffect(() => {
    if (settling || asking) return;
    go();
  }, [settling, asking, spent]);

  const agree = (): void => {
    if (!(ageOk && tosOk)) return;
    void emberApi.visitorConsent()
      .then(() => noteVisitorConsent(new Date().toISOString()))
      .catch(() => trackFailed('talk_consent', 'record_failed'))
      .finally(go);
  };

  if (!asking) return <div className="ember-page-quiet" aria-busy="true" />;
  return (
    <div className="talk-consent" data-log-seen="talk_consent_gate" data-log-discovery={discovery}>
      <EmberDisc face="warm" />
      {/* The one heading and line every door asks the boxes under (2026-10-23 jam, ConsentTerms). */}
      <h1 className="talk-consent-title">{CONSENT_TITLE}</h1>
      <p className="talk-consent-copy">{CONSENT_NOTE}</p>
      <label className="talk-consent-check">
        <input type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} data-log="talk_consent_age" data-log-discovery={discovery} />
        <span>I&rsquo;m 18 or older</span>
      </label>
      <label className="talk-consent-check">
        <input type="checkbox" checked={tosOk} onChange={(e) => setTosOk(e.target.checked)} data-log="talk_consent_terms" data-log-discovery={discovery} />
        <span>
          I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>
          {' '}and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
        </span>
      </label>
      <button type="button" className="ember-btn ember-btn-primary talk-consent-go" disabled={!(ageOk && tosOk)} onClick={agree}>
        Talk to Ember
      </button>
    </div>
  );
}
